/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Ticket, Plus, Copy, CheckCircle2, Clock, Trophy, Gift, Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getInviteQuota, generateInviteCodes, listMyInviteCodes,
} from '@/lib/inviteCodes';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const STATUS_META = {
  active: { label: 'Attivo', variant: 'success', Icon: CheckCircle2 },
  redeemed: { label: 'Riscattato', variant: 'secondary', Icon: Gift },
  expired: { label: 'Scaduto', variant: 'outline', Icon: Clock },
};

const CodeChip = ({ code, status, note, createdAt, redeemedAt, redeemerRenewed, onCopy }) => {
  const meta = STATUS_META[status] || STATUS_META.active;
  const Icon = meta.Icon;
  return (
    <Card className={cn('overflow-hidden', status === 'expired' && 'opacity-60')} data-testid={`invite-code-${code}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <code className="font-mono font-bold text-lg tracking-widest">{code}</code>
          <Button size="icon" variant="ghost" onClick={() => onCopy(code)} data-testid={`copy-${code}`}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={meta.variant} className="text-[10px] flex items-center gap-1">
            <Icon className="h-3 w-3" /> {meta.label}
          </Badge>
          {redeemerRenewed && (
            <Badge variant="secondary" className="text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
              <Trophy className="h-3 w-3 mr-0.5" /> Rinnovato
            </Badge>
          )}
        </div>
        {note && <p className="text-xs text-muted-foreground truncate">{note}</p>}
        <div className="text-[10px] text-muted-foreground">
          Creato {formatDate(createdAt)}
          {redeemedAt && <span> · Riscattato {formatDate(redeemedAt)}</span>}
        </div>
      </CardContent>
    </Card>
  );
};

const StatBox = ({ label, value, icon: Icon, tone = 'default' }) => {
  const tones = {
    default: 'text-primary',
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
  };
  return (
    <div className="rounded-xl border border-border p-3" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={cn('font-display font-bold text-2xl mt-1 tabular-nums', tones[tone])}>{value}</div>
    </div>
  );
};

export const InviteCodesPanel = () => {
  const [quota, setQuota] = useState(null);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, redeemed: 0, renewed_after_free: 0, bonus_months_earned: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(1);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, mine] = await Promise.all([getInviteQuota(), listMyInviteCodes()]);
      setQuota(q);
      setCodes(mine.items || []);
      setStats(mine.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Riservato ai professionisti sanitari');
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!count || count < 1) return;
    setBusy(true);
    try {
      await generateInviteCodes(count, note || null);
      toast.success(`${count} codice/i generato/i`);
      setNote('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (code) => {
    const url = `${window.location.origin}/register?invite=${code}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success(`Link invito copiato per ${code}`),
      () => toast.error('Copia non riuscita'),
    );
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-40 rounded-2xl" />
      </div>
    );
  }

  if (!quota) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          Questa sezione è riservata ai professionisti sanitari abbonati.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="invite-codes-panel">
      {/* Header: quota + explanation */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="font-display flex items-center gap-2">
                <Ticket className="h-5 w-5 text-primary" /> I tuoi codici invito
              </CardTitle>
              <CardDescription>
                Regala {quota.premium_grant_days} giorni di Premium ai tuoi pazienti.
                {' '}Ogni {quota.retention_bonus_threshold} rinnovi = 1 mese gratis di abbonamento per te.
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-xs">
              Tier {quota.tier}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Disponibili" value={quota.remaining} icon={Ticket} tone="success" />
          <StatBox label="Riscattati" value={stats.redeemed || 0} icon={Gift} />
          <StatBox label="Rinnovi post-free" value={stats.renewed_after_free || 0} icon={CheckCircle2} tone="success" />
          <StatBox label="Mesi bonus" value={stats.bonus_months_earned || 0} icon={Trophy} tone="warning" />
        </CardContent>
      </Card>

      {/* Generator */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Genera nuovi codici
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_auto] gap-2 items-end">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Quantità</label>
              <Input
                type="number"
                min={1}
                max={quota.remaining}
                value={count}
                onChange={(e) => setCount(Number(e.target.value) || 1)}
                data-testid="invite-generate-count"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Nota (opzionale)</label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="es. Pazienti diabetici · Ottobre"
                maxLength={120}
                data-testid="invite-generate-note"
              />
            </div>
            <Button
              onClick={generate}
              disabled={busy || count < 1 || count > quota.remaining}
              data-testid="invite-generate-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Genera
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            I codici sono validi 12 mesi. Il link di condivisione formato "/register?invite=CODE" viene copiato automaticamente sul bottone "Copia".
          </p>
        </CardContent>
      </Card>

      {/* Codes list */}
      {codes.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Ticket className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Non hai ancora generato codici invito. Genera il primo qui sopra.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div>
          <h3 className="font-display font-semibold text-sm mb-2">Codici generati ({codes.length})</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {codes.map((c) => (
              <CodeChip
                key={c.id}
                code={c.code}
                status={c.status}
                note={c.note}
                createdAt={c.created_at}
                redeemedAt={c.redeemed_at}
                redeemerRenewed={c.redeemer_renewed}
                onCopy={copyCode}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteCodesPanel;
